(() => {
  "use strict";

  const SECTION_SELECTOR =
    "[data-digi-club-faq]";

  const ITEM_SELECTOR =
    "[data-digi-club-faq-item]";

  const REVEAL_SELECTOR =
    "[data-digi-club-faq-reveal]";

  const instances = new WeakMap();

  class DigiClubFaq {

    constructor(section) {

      this.section = section;

      this.items = Array.from(
        section.querySelectorAll(
          ITEM_SELECTOR
        )
      );

      this.revealElements = Array.from(
        section.querySelectorAll(
          REVEAL_SELECTOR
        )
      );

      this.singleOpen =
        section.dataset.singleOpen === "true";

      this.revealObserver = null;

      this.handleToggle =
        this.handleToggle.bind(this);

      this.init();

    }

    init() {

      this.bindEvents();

      this.setupReveal();

    }

    bindEvents() {

      this.items.forEach((item) => {

        item.addEventListener(
          "toggle",
          this.handleToggle
        );

      });

    }

    unbindEvents() {

      this.items.forEach((item) => {

        item.removeEventListener(
          "toggle",
          this.handleToggle
        );

      });

    }

    handleToggle(event) {

      const current =
        event.currentTarget;

      current.classList.toggle(
        "is-active",
        current.open
      );

      if (
        !this.singleOpen ||
        !current.open
      ) {

        return;

      }

      this.items.forEach((item) => {

        if (item === current) {

          return;

        }

        item.open = false;

        item.classList.remove(
          "is-active"
        );

      });

    }

    setupReveal() {

      const reducedMotion =
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

      const elements = [

        ...this.revealElements,

        ...this.items

      ];

      if (!elements.length) {

        return;

      }

      if (

        reducedMotion ||

        !("IntersectionObserver" in window)

      ) {

        this.showAll();

        return;

      }

      this.revealObserver =
        new IntersectionObserver(

          (entries, observer) => {

            entries.forEach((entry) => {

              if (!entry.isIntersecting) {

                return;

              }

              const element =
                entry.target;

              let delay = 0;

              if (
                element.matches(
                  ITEM_SELECTOR
                )
              ) {

                const index =
                  this.items.indexOf(
                    element
                  );

                delay =
                  Math.min(
                    index * 70,
                    420
                  );

              }

              window.setTimeout(() => {

                if (
                  !element.isConnected
                ) {

                  return;

                }

                element.classList.add(
                  "is-visible"
                );

              }, delay);

              observer.unobserve(
                element
              );

            });

          },

          {

            threshold:0.15,

            rootMargin:
              "0px 0px -8% 0px"

          }

        );

      elements.forEach((element) => {

        this.revealObserver.observe(
          element
        );

      });

    }

    showAll() {

      [

        ...this.revealElements,

        ...this.items

      ].forEach((element) => {

        element.classList.add(
          "is-visible"
        );

      });

    }

    selectBlock(blockId) {

      if (!blockId) {

        return;

      }

      const item =
        this.items.find(

          (element) =>

            String(
              element.dataset.blockId
            ) ===
            String(blockId)

        );

      if (!item) {

        return;

      }

      if (this.singleOpen) {

        this.items.forEach((faq) => {

          faq.open = false;

          faq.classList.remove(
            "is-active"
          );

        });

      }

      item.open = true;

      item.classList.add(
        "is-active",
        "is-visible"
      );

      this.scrollToItem(item);

    }

    deselectBlock(blockId) {

      if (!blockId) {

        return;

      }

      const item =
        this.items.find(

          (element) =>

            String(
              element.dataset.blockId
            ) ===
            String(blockId)

        );

      if (!item) {

        return;

      }

      item.classList.remove(
        "is-active"
      );

    }

    scrollToItem(item) {

      const reducedMotion =
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

      item.scrollIntoView({

        behavior:
          reducedMotion
            ? "auto"
            : "smooth",

        block:"center",

        inline:"nearest"

      });

    }

    destroy() {

      this.unbindEvents();

      if (this.revealObserver) {

        this.revealObserver.disconnect();

        this.revealObserver = null;

      }

      this.items.forEach((item) => {

        item.classList.remove(

          "is-active",

          "is-visible"

        );

      });

      this.items = [];

      this.revealElements = [];

      this.section = null;

    }

  }

  function initializeSection(section) {

    if (

      !section ||

      instances.has(section)

    ) {

      return;

    }

    instances.set(

      section,

      new DigiClubFaq(section)

    );

  }

  function initialize(root) {

    if (!root) {

      return;

    }

    if (

      root.matches?.(
        SECTION_SELECTOR
      )

    ) {

      initializeSection(root);

    }

    root
      .querySelectorAll?.(
        SECTION_SELECTOR
      )
      .forEach(
        initializeSection
      );

  }

  function unload(root) {

    if (!root) {

      return;

    }

    const sections = [];

    if (

      root.matches?.(
        SECTION_SELECTOR
      )

    ) {

      sections.push(root);

    }

    root
      .querySelectorAll?.(
        SECTION_SELECTOR
      )
      .forEach((section) => {

        sections.push(section);

      });

    sections.forEach((section) => {

      const instance =
        instances.get(section);

      if (!instance) {

        return;

      }

      instance.destroy();

      instances.delete(section);

    });

  }

  function getSection(event) {

    return (

      event.target?.closest?.(
        SECTION_SELECTOR
      ) || null

    );

  }

  document.addEventListener(
    "shopify:block:select",
    (event) => {

      const section =
        getSection(event);

      if (!section) {

        return;

      }

      instances
        .get(section)
        ?.selectBlock(
          event.detail.blockId
        );

    }
  );

  document.addEventListener(
    "shopify:block:deselect",
    (event) => {

      const section =
        getSection(event);

      if (!section) {

        return;

      }

      instances
        .get(section)
        ?.deselectBlock(
          event.detail.blockId
        );

    }
  );

  document.addEventListener(
    "shopify:section:load",
    (event) => {

      initialize(event.target);

    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {

      unload(event.target);

    }
  );

  function ready() {

    initialize(document);

  }

  if (
    document.readyState ===
    "loading"
  ) {

    document.addEventListener(
      "DOMContentLoaded",
      ready,
      {
        once:true
      }
    );

  } else {

    ready();

  }

})();